/**
 * WebSocket Client for real-time streaming
 * Handles connection management, reconnection, and message routing
 */

import { io, Socket } from 'socket.io-client';
import { config } from '../config/env';
import type { WSClientMessage, WSServerMessage } from '../types';

type MessageHandler = (message: WSServerMessage) => void;
type ConnectionHandler = () => void;
type ErrorHandler = (error: Error) => void;

class WebSocketClient {
  private socket: Socket | null = null;
  private messageHandlers: Set<MessageHandler> = new Set();
  private connectionHandlers: Set<ConnectionHandler> = new Set();
  private disconnectionHandlers: Set<ConnectionHandler> = new Set();
  private errorHandlers: Set<ErrorHandler> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectDelay = 1000; // Start with 1 second

  connect(): void {
    if (this.socket?.connected) {
      return;
    }

    this.socket = io(config.wsUrl, {
      withCredentials: true,
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
      reconnectionDelayMax: 5000,
    });

    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      this.connectionHandlers.forEach((handler) => handler());
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      this.disconnectionHandlers.forEach((handler) => handler());

      // Attempt manual reconnection with exponential backoff
      if (reason === 'io server disconnect' || reason === 'io client disconnect') {
        this.attemptReconnect();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      this.errorHandlers.forEach((handler) => handler(error));
      this.attemptReconnect();
    });

    // Handle all server message types
    this.socket.on('token', (data) => this.handleMessage({ type: 'token', ...data }));
    this.socket.on('complete', (data) => this.handleMessage({ type: 'complete', ...data }));
    this.socket.on('error', (data) => this.handleMessage({ type: 'error', ...data }));
    this.socket.on('model_switched', (data) => this.handleMessage({ type: 'model_switched', ...data }));
    this.socket.on('pong', () => this.handleMessage({ type: 'pong' }));

    // Handle comparison events
    this.socket.on('compare_token', (data) => this.handleMessage({ type: 'compare_token', ...data }));
    this.socket.on('compare_complete', (data) => this.handleMessage({ type: 'compare_complete', ...data }));
    this.socket.on('compare_error', (data) => this.handleMessage({ type: 'compare_error', ...data }));
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.errorHandlers.forEach((handler) =>
        handler(new Error('Failed to reconnect after maximum attempts'))
      );
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);

    setTimeout(() => {
      if (!this.socket?.connected) {
        this.socket?.connect();
      }
    }, delay);
  }

  private handleMessage(message: WSServerMessage): void {
    this.messageHandlers.forEach((handler) => handler(message));
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  send(message: WSClientMessage): void {
    if (!this.socket?.connected) {
      throw new Error('WebSocket is not connected');
    }

    this.socket.emit(message.type, message);
  }

  // Send a message to the LLM, optionally with file attachments
  sendMessage(conversationId: string, content: string, modelId: string, fileIds?: string[]): void {
    this.send({
      type: 'message',
      conversationId,
      content,
      modelId,
      ...(fileIds && fileIds.length > 0 && { fileIds }),
    });
  }

  // Switch model in a conversation
  switchModel(conversationId: string, modelId: string): void {
    this.send({
      type: 'model_switch',
      conversationId,
      modelId,
    });
  }

  // Cancel streaming response
  cancelStream(conversationId: string): void {
    this.send({
      type: 'cancel',
      conversationId,
    });
  }

  // Send heartbeat ping
  ping(): void {
    this.send({ type: 'ping' });
  }

  // Send comparison request (two models, same prompt)
  sendCompare(compareId: string, content: string, modelIds: [string, string]): void {
    this.send({
      type: 'compare',
      compareId,
      content,
      modelIds,
    });
  }

  // Cancel a running comparison
  cancelCompare(compareId: string): void {
    this.send({
      type: 'compare_cancel',
      compareId,
    });
  }

  // Event handler registration
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  onConnect(handler: ConnectionHandler): () => void {
    this.connectionHandlers.add(handler);
    return () => this.connectionHandlers.delete(handler);
  }

  onDisconnect(handler: ConnectionHandler): () => void {
    this.disconnectionHandlers.add(handler);
    return () => this.disconnectionHandlers.delete(handler);
  }

  onError(handler: ErrorHandler): () => void {
    this.errorHandlers.add(handler);
    return () => this.errorHandlers.delete(handler);
  }

  // Connection state
  get isConnected(): boolean {
    return this.socket?.connected || false;
  }

  get connectionState(): 'connected' | 'disconnected' | 'connecting' {
    if (!this.socket) return 'disconnected';
    if (this.socket.connected) return 'connected';
    return 'connecting';
  }
}

// Export singleton instance
export const wsClient = new WebSocketClient();
